import React from "react";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useExpirationText } from "utils/hooks/useExpirationText";

interface ExpirationStatusTextProps {
  validUntil?: string;
  isExpired?: boolean;
  isHistory?: boolean;
  isAvailable?: boolean;
  warnWithinDays?: number;
  className?: string;
}

const ExpirationStatusText: React.FC<ExpirationStatusTextProps> = ({
  validUntil,
  isExpired = false,
  isHistory = false,
  isAvailable = true,
  warnWithinDays = 3,
  className,
}) => {
  const { locale } = useRouter();
  const t = useTranslations();

  const expirationText = useExpirationText({
    validUntil: validUntil || new Date().toISOString(),
    isExpired,
  });

  const now = new Date();
  const normalizedValidUntil = validUntil ? new Date(validUntil) : null;
  const remainingDays =
    normalizedValidUntil && !Number.isNaN(normalizedValidUntil.getTime())
      ? Math.ceil((normalizedValidUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      : null;

  const isExpiringSoon =
    !isHistory &&
    !isExpired &&
    isAvailable &&
    remainingDays !== null &&
    remainingDays < warnWithinDays;

  const colorClassName = isExpiringSoon ? "text-red-500" : "text-[#4F2F18]";

  const combinedClassName = [className, colorClassName].filter(Boolean).join(" ");

  const contentText = isHistory
    ? `${t("Expired on")} ${now.getDate().toString().padStart(2, "0")} ${now
        .toLocaleString(locale?.split("-")[0] || "en-US", { month: "short" })
        .toUpperCase()} ${now.getFullYear().toString().slice(-2)}`
    : expirationText;

  return <p className={combinedClassName}>{contentText}</p>;
};

export default ExpirationStatusText;
